import { FC, MouseEvent } from "react"

import { ReactComponent as IconMinus } from "@common_assets/svg/minus.svg"
import { ReactComponent as IconPlus } from "@common_assets/svg/plus.svg"
import { IconButton, Stack, Typography } from "@mui/material"

interface IProps {
  quantityText: string
  decreaseLabel: string
  increaseLabel: string
  onDecrease: (event: MouseEvent<HTMLButtonElement>) => void
  onIncrease: () => void
  // Set when a cap blocks adding: id of the helper explaining why.
  increaseBlockedReasonId?: string
}

const stepButtonSx = {
  width: "24px",
  height: "24px",
  p: 0,
  border: "1px solid",
  borderColor: "primary.main",
  bgcolor: "mockup.primary99",
  color: "primary.main",
  "&:hover": { bgcolor: "mockup.primary95" },
  "& svg": { width: "16px", height: "16px", display: "block" },
}

const QuantityStepper: FC<IProps> = ({
  quantityText,
  decreaseLabel,
  increaseLabel,
  onDecrease,
  onIncrease,
  increaseBlockedReasonId,
}) => {
  const increaseBlocked = !!increaseBlockedReasonId

  return (
    <Stack direction="row" alignItems="center" gap="4px">
      <IconButton
        aria-label={decreaseLabel}
        onClick={(event) => {
          event.stopPropagation()
          onDecrease(event)
        }}
        sx={stepButtonSx}
      >
        <IconMinus aria-hidden />
      </IconButton>
      <Typography
        variant="body3"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "24px",
          height: "24px",
          px: 1,
          borderRadius: "12px",
          bgcolor: "primary.main",
          color: "mockup.primary99",
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
        }}
      >
        {quantityText}
      </Typography>
      <IconButton
        aria-label={increaseLabel}
        aria-disabled={increaseBlocked || undefined}
        aria-describedby={increaseBlockedReasonId}
        onClick={(event) => {
          event.stopPropagation()
          if (!increaseBlocked) onIncrease()
        }}
        sx={{
          ...stepButtonSx,
          ...(increaseBlocked && { opacity: 0.5, cursor: "not-allowed" }),
        }}
      >
        <IconPlus aria-hidden />
      </IconButton>
    </Stack>
  )
}

export default QuantityStepper
